// components/Toolbar/tools/SelectTool.js
import React, { useEffect, useState } from 'react';
import HandIcon from '../../images/Icon20_Pan.svg';
import Button from '../Toolbar/Button';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import {
  setActiveObject,
  setCursorTooltip,
  updateTool,
} from '../../../../redux/actions/canvas_editor';
import InfoBox from './InfoBox';
import { __, setTableParams, setURLParam } from '../../../../core/utils';
import { withRouter } from 'react-router';
import { resetObjectStyle } from '../../helpers/fabricHelpers';

const Hand = ({
  canvas_editor: { activeTool, viewer, fabricCanvases },
  source: { stage_id, scale1, scale2 },
  table,
  match,
  task_table,
  sourceType,
}) => {
  const isActive = activeTool == 'hand';

  const table_name =
    match.path == '/plans/revisions/:plan_revision'
      ? 'current_set_plans'
      : 'working_set_plans';

  const {
    pinned_tasks,
    pinned_plans,
    pinned_specifications,
    clicked_row,
    showFilters,
  } = table[table_name];

  const [infoBox, setInfoBox] = useState(null);

  const handleClick = () => {
    viewer?.setMouseNavEnabled(true);
    updateTool({ tool: 'hand' });
  };

  const _handleReadTask = (taskID) => {
    setURLParam('task_id', taskID);
    setTableParams('all_tasks', {
      clicked_row: taskID,
    });
  };

  const _handleReadSpecification = (specificationID) => {
    setURLParam('specification_id', specificationID);

    setTableParams(
      table_name == 'current_set_plans'
        ? 'current_set_specifications'
        : 'working_set_specifications',
      {
        clicked_row: specificationID,
      }
    );
  };

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.selection = false;

      setCursorTooltip('');

      canvas.forEachObject((obj) => {
        obj.selectable = false;
        obj.evented = true;

        obj.setCoords();
      });

      canvas.defaultCursor = 'grab';
      canvas.hoverCursor = 'pointer';
      canvas.moveCursor = 'grabbing';

      const activeObject = canvas.getActiveObject();
      if (activeObject?.type === 'activeSelection') {
        activeObject.getObjects().forEach((obj) => {
          resetObjectStyle(obj);
        });
        canvas.discardActiveObject();
        setActiveObject(null);
      } else if (activeObject) {
        resetObjectStyle(activeObject);
        canvas.discardActiveObject();
        setActiveObject(null);
      }

      canvas.requestRenderAll();

      function handleMouseDown(obj, e) {
        if (!obj || !obj.objectType || obj.objectType === 'task') {
          viewer?.setMouseNavEnabled(true);

          return setInfoBox(null);
        }

        if (obj.objectType === 'plan') {
          obj = { ...obj, plan: pinned_plans[obj.planID] };
        }

        setInfoBox({
          object: obj,
          position: {
            left: e.clientX - 25,
            top: e.clientY + 20,
          },
        });

        setCursorTooltip('');

        viewer?.setMouseNavEnabled(true);
      }

      function handleMouseOver(e) {
        const obj = e.target;

        if (!obj) return;

        if (obj.objectType == 'task') {
          const task = pinned_tasks[obj.taskID];

          const identifier = 'T-' + task.identifier;
          const title = task.title;

          return setCursorTooltip(
            <>
              {identifier}
              <br />
              {title}
            </>
          );
        }

        if (obj.objectType == 'specification') {
          const { title } = pinned_specifications[obj.specificationCodeID];

          return setCursorTooltip(<>{title}</>);
        }

        setCursorTooltip(__('canvas.tooltip.hand'));
      }

      function handleMouseOut(e) {
        setCursorTooltip('');
      }

      const activateSelectForObject = (target) => {
        if (
          !target ||
          _.includes(['arrow-head', 'controlpoint', 'highlight'], target.objectType)
        ) {
          return;
        }

        handleCloseInfo();
        viewer?.setMouseNavEnabled(false);
        canvas.setActiveObject(target);
        canvas.requestRenderAll();
        setActiveObject(target);
        updateTool({ tool: 'select' });
      };

      const onPointerDown = (e) => {
        const target = canvas.findTarget(e);

        if (target?.objectType === 'task') {
          setTableParams(table_name, {
            showFilters: undefined,
            clicked_row: undefined,
          });
          return _handleReadTask(target.taskID);
        }

        if (target?.objectType === 'specification') {
          const { specification_id } =
            pinned_specifications[target.specificationCodeID];

          setTableParams(table_name, {
            showFilters: undefined,
            clicked_row: undefined,
          });
          return _handleReadSpecification(specification_id);
        }

        handleMouseDown(target, e);

        // viewer?.setMouseNavEnabled(false)
        // if (e.pointerType === 'touch') {
        //   if (e.touches && e.touches.length == 2) {
        //     handleMouseDown({ e });
        //   } else if (e.touches && e.touches.length >= 2) {
        //     handleMouseDown({ e });
        //   }
        // } else {
        //   viewer?.setMouseNavEnabled(false);
        // }
      };

      const handleMouseDblClick = (event) => {
        activateSelectForObject(event?.target);
      };

      canvas.on('mouse:over', handleMouseOver);
      canvas.on('mouse:out', handleMouseOut);
      canvas.on('mouse:dblclick', handleMouseDblClick);
      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);

      disposers.push(() => {
        if (canvas?.upperCanvasEl)
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );

        canvas.off('mouse:over', handleMouseOver);
        canvas.off('mouse:out', handleMouseOut);
        canvas.off('mouse:dblclick', handleMouseDblClick);

        setInfoBox(null);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [isActive, pinned_plans, fabricCanvases]);

  useEffect(() => {
    if (clicked_row || task_table.clicked_row || showFilters) {
      handleCloseInfo();
    }
  }, [clicked_row, task_table, showFilters]);

  const handleCloseInfo = () => setInfoBox(null);

  const handleEdit = () => {
    const {
      left,
      top,
      planID,
      source: { title },
    } = infoBox.object;

    setTableParams(table_name, {
      x: left,
      y: top,
      is_edit: planID,
      plan_title: title,
      attach_plan_wizard: true,
      stage_id: stage_id,
    });

    handleCloseInfo();
  };

  return (
    <>
      <Button
        id='hand'
        label={__('canvas.toolbar.hand')}
        icon={HandIcon}
        active={isActive}
        onClick={handleClick}
        extraClass={['roundedUp']}
      />

      {infoBox && (
        <InfoBox
          infoBox={infoBox}
          onClose={handleCloseInfo}
          sourceType={sourceType}
          plan={{
            scale2: scale2 || null,
            scale1: scale1 || null,
          }}
          onEdit={handleEdit}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canvas_editor: state.canvas_editor,
    table: state.table,
    working_set_plans: state.table['working_set_plans'],
    task_table: state.table['all_tasks'],
  };
};

export default withRouter(connect(mapStateToProps, null)(Hand));
